import { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { useSettings } from "../lib/useSettings";
import { usePageContent } from "../lib/usePageContent";

const DEFAULT_HERO =
  "";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  subject: "Informazioni generali",
  message: "",
};

const DEFAULT_SUBJECTS = [
  "Informazioni generali",
  "Opportunità di sviluppo",
  "Partnership",
  "Press & Media",
  "Carriera",
];

export default function Contatti() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const settings = useSettings();
  const c = usePageContent("contatti", {});
  const heroImg = settings?.hero_contact || DEFAULT_HERO;
  const subjects = Array.isArray(c.form_subjects) && c.form_subjects.length ? c.form_subjects : DEFAULT_SUBJECTS;

  const submit = async (e) => {
    e.preventDefault();
    if (!privacyConsent) {
      toast.error("Devi accettare l'informativa privacy per inviare il messaggio.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Messaggio inviato.", {
        description: "Ti risponderemo entro 2 giorni lavorativi.",
      });
      setForm(INITIAL);
      setPrivacyConsent(false);
    } catch (err) {
      toast.error("Invio non riuscito", {
        description: "Riprova tra qualche minuto.",
      });
    } finally {
      setLoading(false);
    }
  };

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="contatti-page">
      <SEO
        title="Contatti"
        path="/contatti"
        description="Contatta Invest Hospitality per opportunità di sviluppo, partnership, progetti di rigenerazione urbana. Sede: Via della Moscova 18, Milano."
        image={heroImg}
      />
      <Toaster theme="dark" position="top-right" richColors />
      <PageHero
        eyebrow={settings?.hero_contact_eyebrow || "Contatti"}
        title={
          settings?.hero_contact_title || "Parliamone.\n*Milano ci aspetta.*"
        }
        subtitle={
          settings?.hero_contact_subtitle ||
          "Scrivici per opportunità di sviluppo, partnership, progetti di rigenerazione urbana o richieste di informazioni."
        }
        image={heroImg}
      />

      <section className="relative py-16 md:py-24 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-10">
            <ContactBlock
              icon={<MapPin size={16} />}
              label={c.address_label || "Sede operativa"}
              value={c.address_value || "Via della Moscova 18"}
              sub={c.address_sub || "20121 Milano · Italia"}
            />
            <ContactBlock
              icon={<Mail size={16} />}
              label={c.email_label || "Email"}
              value={c.email_value || "info@investhospitality.it"}
              sub={c.email_sub || "Ti rispondiamo in 48h"}
            />
            <ContactBlock
              icon={<Phone size={16} />}
              label={c.phone_label || "Telefono"}
              value={c.phone_value || "+39 02 0000 0000"}
              sub={c.phone_sub || "Lun–Ven · 9.00–18.30"}
            />

            <div className="rounded-2xl border border-white/10 bg-[#0F0F14] p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56]">
                {c.press_eyebrow || "Stampa & Media"}
              </p>
              <p className="mt-3 text-white/80">
                {c.press_email || "press@investhospitality.it"}
              </p>
              <p className="mt-2 text-white/50 text-sm">
                {c.press_sub || "Per richieste urgenti, materiali stampa e interviste."}
              </p>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={submit}
              className="rounded-2xl border border-white/10 bg-[#0A0A0D] p-8 md:p-10 space-y-6"
              data-testid="contact-form"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field
                  label="Nome e cognome"
                  required
                  value={form.name}
                  onChange={onChange("name")}
                  testId="contact-name"
                />
                <Field
                  label="Email"
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange("email")}
                  testId="contact-email"
                />
              </div>
              <Field
                label="Telefono (opzionale)"
                value={form.phone}
                onChange={onChange("phone")}
                testId="contact-phone"
              />
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                  Oggetto
                </label>
                <select
                  value={form.subject}
                  onChange={onChange("subject")}
                  data-testid="contact-subject"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-3 text-base appearance-none"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 4px center",
                  }}
                >
                  {subjects.map((s) => (
                    <option key={s} value={s} className="bg-[#0A0A0D]">
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                  Messaggio
                </label>
                <textarea
                  rows={5}
                  required
                  value={form.message}
                  onChange={onChange("message")}
                  data-testid="contact-message"
                  placeholder="Raccontaci il tuo progetto…"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-3 text-base resize-none"
                />
              </div>
              <div className="pt-2 space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    checked={privacyConsent}
                    onChange={(e) => setPrivacyConsent(e.target.checked)}
                    data-testid="contact-privacy-consent"
                    className="mt-1 h-4 w-4 accent-[#C48F56] shrink-0"
                  />
                  <span className="text-xs text-white/55 leading-relaxed">
                    Ho letto e accetto l'
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/80 underline decoration-dotted underline-offset-2 hover:text-[#C48F56]"
                    >
                      Informativa Privacy
                    </a>{" "}
                    e acconsento al trattamento dei dati personali per la
                    finalità di evasione della richiesta (art. 6.1.a GDPR).
                  </span>
                </label>
                <div className="flex items-center justify-end">
                  <button
                    type="submit"
                    disabled={loading || !privacyConsent}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-2 rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:bg-[#C48F56] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? "Invio…" : "Invia messaggio"}
                    <Send size={14} />
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
        {label}
      </label>
      <input
        data-testid={testId}
        className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-3 text-base"
        {...props}
      />
    </div>
  );
}

function ContactBlock({ icon, label, value, sub }) {
  return (
    <div>
      <div className="flex items-center gap-3 text-[#C48F56]">
        {icon}
        <span className="font-mono text-[10px] uppercase tracking-[0.25em]">
          {label}
        </span>
      </div>
      <div className="mt-3 font-display text-3xl md:text-4xl tracking-tight">
        {value}
      </div>
      {sub && <div className="mt-1 text-white/40 text-sm">{sub}</div>}
    </div>
  );
}
